/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { EntityLimitationsService } from '../../../services/entity-limitations.service';
import { IEntityLimitations } from '../../../../shared/models/info/entity-limitations';
import { IFieldLimitations } from '../../../../shared/models/info/field-limitations';
import { LanguageIconComponent } from '../../icons/language-icon/language-icon.component';
import { TrackUnsavedFieldDirective } from '../../../directives/track-unsaved-field.directive';
import { LabelWithHelp } from '../label-with-help/label-with-help';

@Component({
  selector: 'app-text-area',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LanguageIconComponent,
    TrackUnsavedFieldDirective,
    LabelWithHelp,
  ],
  templateUrl: './text-area.component.html',
  styleUrl: './text-area.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextAreaComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: TextAreaComponent,
    },
  ],
})
export class TextAreaComponent implements OnInit, ControlValueAccessor, Validator, OnDestroy {
  @Input() public label?: string;
  @Input() public warning?: string;
  @Input() public placeholder?: string;
  @Input() public value?: string;
  @Input() public maxRows = 3;
  @Input() public maxRowAndLengthControlIsActive = false;
  @Input() public maxLengthPerRow = 36;
  @Input() public fieldIsRequired = false;
  @Input() public maxLength?: number;
  @Input() public fieldConfigurationAddress?: string;
  @Input() public rows = 4;
  @Input() public readonly = false;
  @Input() public isTranslatable = false;
  /**
   * Default: border-gray-300 focus:ring-amazone-orange focus:border-amazone-orange
   */
  @Input() public borderColorClass = 'border-gray-300 focus:ring-amazone-orange focus:border-amazone-orange';
  @Input() public reset$?: Subject<unknown>;
  /**
   * Default: true
   */
  @Input() public displayChangeIndicator = true;

  public textAreaControl!: FormControl;
  public number = Number;
  public maxLengthWithoutLineSeparators = this.maxLength;

  private entityLimitationsService = inject(EntityLimitationsService);
  private excludeLineSeparatorsFromLengthCalculation = false;
  private textAreaValueChanges: Subscription | null = null;
  private enitityLimitationServiceSubscription: Subscription | null = null;
  private intialized = false;
  private changeCounter = 0;

  public ngOnInit(): void {
    this.textAreaControl = new FormControl('', []);

    if (this.value) {
      this.textAreaControl.setValue(this.value);
    }

    this.textAreaValueChanges = this.textAreaControl.valueChanges.subscribe((value) => {
      if (this.intialized) {
        if (this.changeCounter > 0) {
          this.onChange(value);
        }
        this.changeCounter += 1;
      }
    });

    if (this.fieldConfigurationAddress) {
      this.enitityLimitationServiceSubscription = this.entityLimitationsService
        .getEntityLimitations$()
        .subscribe((entityLimitations) => {
          this.configureLengthControl(this.filterFieldLimitations(entityLimitations));
        });
    } else {
      this.activateLengthControl();
    }
  }

  public ngOnDestroy(): void {
    if (this.textAreaValueChanges) {
      this.textAreaValueChanges.unsubscribe();
      this.textAreaValueChanges = null;
    }
    if (this.enitityLimitationServiceSubscription) {
      this.enitityLimitationServiceSubscription.unsubscribe();
    }
  }

  public writeValue(value: string): void {
    this.textAreaControl.setValue(value || '');
    if (!value) {
      this.textAreaControl.markAsUntouched();
    }
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.textAreaControl.disable();
    } else {
      this.textAreaControl.enable();
    }
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    console.debug('Validating text area', control);
    return this.textAreaControl.errors;
  }

  public enforceLineLimit(event: Event): void {
    if (!this.maxRowAndLengthControlIsActive) {
      return;
    }
    const textarea = event.target as HTMLTextAreaElement;
    const lines = textarea.value.split('\n');
    if (lines.length > this.maxRows) {
      textarea.value = lines.slice(0, this.maxRows).join('\n');
      this.textAreaControl.setValue(textarea.value);
    }
  }

  public calculateTextLength(text?: string): number {
    if (!text) {
      return 0;
    }
    if (this.excludeLineSeparatorsFromLengthCalculation) {
      text = text.replaceAll('\r', '').replaceAll('\n', '');
    }
    return text.length;
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (!this.maxRowAndLengthControlIsActive) {
      return;
    }
    if (event.key != 'Enter') {
      return;
    }

    const textarea = event.target as HTMLTextAreaElement;
    const lineCount = textarea.value.split('\n').length;
    if (lineCount == this.maxRows) {
      event.preventDefault();
    }
  }

  private configureLengthControl(fieldLimitations: IFieldLimitations | undefined): void {
    if (!fieldLimitations) {
      return;
    }
    this.maxLength = fieldLimitations.maxLength;
    this.maxLengthPerRow = fieldLimitations.lineMaxLength ?? 0;
    this.maxRows = fieldLimitations.lineCount ?? 0;
    if (this.maxRows > 0) {
      this.maxRowAndLengthControlIsActive = true;
    }
    this.activateLengthControl();
  }

  private activateLengthControl(): void {
    this.maxLengthWithoutLineSeparators = this.maxLength;
    this.excludeLineSeparatorsFromLengthCalculation = false;
    if (this.maxRowAndLengthControlIsActive) {
      this.maxLengthWithoutLineSeparators = this.maxRows * this.maxLengthPerRow;
      this.maxLength = this.maxLengthWithoutLineSeparators + (this.maxRows - 1) * 2;
      this.excludeLineSeparatorsFromLengthCalculation = true;
    }
    this.configureValidators();
    this.intialized = true;
  }

  private filterFieldLimitations(entityLimitations: IEntityLimitations[]): IFieldLimitations | undefined {
    if (!this.fieldConfigurationAddress) {
      return undefined;
    }

    const [entityName, fieldName] = this.fieldConfigurationAddress.split('.');
    if (!entityName || !fieldName) {
      console.error(`fieldConfigurationAddress ${this.fieldConfigurationAddress} invalid`);
      return undefined;
    }

    const entityConfiguration = entityLimitations.find((i) => i.entityName.toLowerCase() === entityName.toLowerCase());
    if (!entityConfiguration) {
      console.error(`Could not find configuration for entity ${entityName}`);
      return undefined;
    }

    const fieldConfiguration = entityConfiguration.fieldLimitations.find(
      (i) => i.fieldName.toLowerCase() === fieldName.toLowerCase()
    );
    if (!fieldConfiguration) {
      console.error(`Could not find configuration for field ${fieldName}`);
    }
    return fieldConfiguration;
  }

  private maxRowsValidator(maxRows: number) {
    return (control: AbstractControl) => {
      const value = control.value || '';
      const rows = value.split('\n');
      return this.maxRowAndLengthControlIsActive && rows.length > maxRows ? { maxRowsExceeded: true } : null;
    };
  }

  private maxLengthPerRowValidator(maxLength: number) {
    return (control: AbstractControl) => {
      const value = control.value || '';
      const rows = value.replaceAll('\r', '').split('\n');
      return this.maxRowAndLengthControlIsActive && rows.some((row: string | string[]) => row.length > maxLength)
        ? { maxLengthPerRowExceeded: true }
        : null;
    };
  }

  private configureValidators(): void {
    const validators = [];
    if (this.fieldIsRequired) {
      validators.push(Validators.required);
    }
    validators.push(this.maxRowsValidator(this.maxRows));
    validators.push(this.maxLengthPerRowValidator(this.maxLengthPerRow));

    this.textAreaControl.clearValidators();
    this.textAreaControl.setValidators(validators);
  }

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
}
